import { useEffect } from 'react';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';
import { githubAuthApiRef, useApi, configApiRef } from '@backstage/core-plugin-api';
import { useAsync } from 'react-use';
import { Octokit } from '@octokit/rest';
import { FieldValidation } from '@rjsf/utils';
import yaml from 'js-yaml';
import { ReturnedData as ComponentInformation } from './ComponentInformation/ComponentInformation';

// Type definition for AI-approved configuration from YAML file
// This accepts any structure from the YAML - templates can access whatever fields exist
export type AIApprovedConfigData = Record<string, any>;

/** Status of the AI shortname validation */
export type AIValidationStatus = 'idle' | 'loading' | 'error' | 'success';

/** Wrapper emitted by AppShortnameAIValidator via onChange */
export interface AIValidationResult {
  /** Current validation status */
  status: AIValidationStatus;
  /** Parsed YAML config data (only present when status is 'success') */
  data: AIApprovedConfigData | null;
  /** Human-readable error message (only present when status is 'error') */
  errorMessage: string | null;
}

interface ScaffolderFormData {
  componentInformation?: ComponentInformation;
  [key: string]: any; // Allow other form fields
}

interface ScaffolderFormContext {
  formData?: ScaffolderFormData;
}

// Hardcoded GitHub repository configuration
const GITHUB_ORG = 'AAInternal';
const GITHUB_REPO = 'GaaS-Policy_as_Code';
const YAML_PATH = 'ai';
const YAML_FILE_EXTENSION = '.yaml';

export const AppShortnameAIValidator = ({
  onChange,
  formContext,
}: FieldExtensionComponentProps<AIValidationResult>) => {
  const ghecAuthApi = useApi(githubAuthApiRef);
  const configApi = useApi(configApiRef);
  const githubUrl = configApi.getString('github.ghec.apiUrl');

  // Access appShortname from the form's componentInformation field
  // formContext.formData contains ALL form data from the current page
  const typedFormContext = formContext as ScaffolderFormContext | undefined;
  const componentInformation = typedFormContext?.formData?.componentInformation;
  const appShortname = componentInformation?.shortName || '';
  // Access useCaseCategory from the componentInformation field (rendered inside ComponentInformation for AIAgent templates)
  const useCaseCategory = componentInformation?.useCaseCategory || '';

  // Use useAsync's built-in state management (loading, error, value)
  const state = useAsync(async () => {
    // Don't validate if we don't have both appShortname AND useCaseCategory
    if (!appShortname || !useCaseCategory) {
      return undefined;
    }

    const ghecAccessToken = await ghecAuthApi.getAccessToken();
    const octokit = new Octokit({
      auth: ghecAccessToken,
      baseUrl: githubUrl,
    });

    // Check if file exists in the repository
    // File path format: ai/appShortname-useCaseCategory.yaml
    const filePath = `${YAML_PATH}/${appShortname}-${useCaseCategory}${YAML_FILE_EXTENSION}`;

    const response = await octokit.repos.getContent({
      owner: GITHUB_ORG,
      repo: GITHUB_REPO,
      path: filePath,
    });

    // Parse the YAML content - accept any structure
    let parsedConfig: AIApprovedConfigData = {};
    if ('content' in response.data && typeof response.data.content === 'string') {
      const content = Buffer.from(response.data.content, 'base64').toString('utf-8');
      const parsed = yaml.load(content);
      // YAML can return null for empty files, undefined for invalid, or an object
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('YAML file is empty or invalid');
      }
      parsedConfig = parsed as AIApprovedConfigData;
    }

    return parsedConfig;
  }, [appShortname, useCaseCategory]);

  // Call onChange based on state changes
  // useEffect ensures this only runs when state actually changes, preventing infinite loops
  useEffect(() => {
    if (!appShortname || !useCaseCategory) {
      onChange({ status: 'idle', data: null, errorMessage: null });
      return;
    }

    if (state.loading) {
      onChange({ status: 'loading', data: null, errorMessage: null });
      return;
    }

    if (state.error) {
      const msg = `${appShortname} (use case: ${useCaseCategory}) has not been approved for AI resources.`;
      onChange({ status: 'error', data: null, errorMessage: msg });
    } else if (state.value !== undefined) {
      onChange({ status: 'success', data: state.value, errorMessage: null });
    } else {
      onChange({ status: 'idle', data: null, errorMessage: null });
    }
  }, [state.loading, state.error, state.value, onChange, appShortname, useCaseCategory]);

  // Rendering is handled by ComponentInformation via formContext
  return null;
};

// Validation function to block form submission when validation fails
export const appShortnameAIValidatorValidation = (value: AIValidationResult | null, validation: FieldValidation) => {
  // Block navigation if validation has not succeeded
  if (!value || value.status !== 'success' || !value.data) {
    validation.addError('Application must be approved for AI resources before proceeding');
  }
};
